import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { LOCALE_CONFIG, LocaleKey, normalizeLocaleKey, initialLocale, ensureLanguageLoaded, detectClientLocale } from '@/i18n';
import { supabase } from '@/integrations/supabase/client';

interface LocaleContextType {
  locale: LocaleKey;
  currency: string;
  currencySymbol: string;
  currencyLocale: string;
  setLocale: (locale: LocaleKey) => void;
  formatPrice: (priceEur: number) => string;
  convertPrice: (priceEur: number) => Promise<number>;
  isConverting: boolean;
}

const LocaleContext = createContext<LocaleContextType | null>(null);

// Cache for exchange rates
const rateCache: Record<string, { rate: number; timestamp: number }> = {};
const CACHE_DURATION = 60 * 60 * 1000; // 1 hour

export const LocaleProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { i18n } = useTranslation();
  // Use the initialLocale from i18n module (which properly handles subdomain detection)
  const [locale, setLocaleState] = useState<LocaleKey>(initialLocale);
  const [exchangeRates, setExchangeRates] = useState<Record<string, number>>({ EUR: 1 });
  const [isConverting, setIsConverting] = useState(false);

  const config = LOCALE_CONFIG[locale];

  // Fetch exchange rate for the current currency
  const fetchExchangeRate = useCallback(async (toCurrency: string) => {
    if (toCurrency === 'EUR') return 1;

    // Check cache
    const cached = rateCache[toCurrency];
    if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
      return cached.rate;
    }

    try {
      setIsConverting(true);
      const { data, error } = await supabase.functions.invoke('exchange-rate', {
        body: { fromCurrency: 'EUR', toCurrency }
      });

      if (error) throw error;

      const rate = data.rate || 1;
      rateCache[toCurrency] = { rate, timestamp: Date.now() };
      setExchangeRates(prev => ({ ...prev, [toCurrency]: rate }));
      return rate;
    } catch (error) {
      console.error('Error fetching exchange rate:', error);
      // Fallback rates
      if (toCurrency === 'BRL') return 6.0;
      if (toCurrency === 'USD') return 1.1;
      return 1;
    } finally {
      setIsConverting(false);
    }
  }, []);

  // Update exchange rates when locale changes
  useEffect(() => {
    if (config.currency !== 'EUR') {
      fetchExchangeRate(config.currency);
    }
  }, [config.currency, fetchExchangeRate]);

  const setLocale = useCallback((newLocale: LocaleKey) => {
    setLocaleState(newLocale);
    // For en-EU, use English translations but with EUR currency
    const i18nLanguage = newLocale === 'en-EU' ? 'en' : newLocale;
    void ensureLanguageLoaded(i18nLanguage).then(() => {
      i18n.changeLanguage(i18nLanguage);
    });
    localStorage.setItem('i18nextLng', newLocale);
  }, [i18n]);

  // Detecta locale real APÓS hidratação (SSR renderiza sempre em 'es' para
  // evitar hydration mismatch). Aqui trocamos para subdomínio/localStorage/navigator.
  useEffect(() => {
    const detected = detectClientLocale();
    if (detected !== locale) {
      setLocaleState(detected);
      const i18nLng = detected === 'en-EU' ? 'en' : detected;
      void ensureLanguageLoaded(i18nLng).then(() => {
        i18n.changeLanguage(i18nLng);
      });
    }
    // Persistir escolha se ainda não persistida
    try {
      const stored = localStorage.getItem('i18nextLng');
      if (!stored) localStorage.setItem('i18nextLng', detected);
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const convertPrice = useCallback(async (priceEur: number): Promise<number> => {
    if (config.currency === 'EUR') return priceEur;

    const rate = exchangeRates[config.currency] || await fetchExchangeRate(config.currency);
    return priceEur * rate;
  }, [config.currency, exchangeRates, fetchExchangeRate]);

  const formatPrice = useCallback((priceEur: number): string => {
    const rate = exchangeRates[config.currency] || 1;
    const convertedPrice = config.currency === 'EUR' ? priceEur : priceEur * rate;

    return new Intl.NumberFormat(config.currencyLocale, {
      style: 'currency',
      currency: config.currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(convertedPrice);
  }, [config, exchangeRates]);

  return (
    <LocaleContext.Provider
      value={{
        locale,
        currency: config.currency,
        currencySymbol: config.currencySymbol,
        currencyLocale: config.currencyLocale,
        setLocale,
        formatPrice,
        convertPrice,
        isConverting
      }}
    >
      {children}
    </LocaleContext.Provider>
  );
};

export const useLocale = () => {
  const context = useContext(LocaleContext);
  if (!context) {
    throw new Error('useLocale must be used within a LocaleProvider');
  }
  return context;
};
